import { Exchange } from '../../types/exchange';
import { BotComplexParams, BotTimeFrames, Candle } from '../Detail/types';
import { IndicatorDataPoint } from '../ProfitCalculations/types';

export enum NextOrderState {
  MultiOrder = 'MULTI_ORDER',
  InitialisedMrcState = 'INITIALISED_MRC_STATE',
  PendingMrcState = 'PENDING_MRC_STATE',
  ResetMrcState = 'RESET_MRC_STATE',
  CbrRange = 'CBR_RANGE',
  OrderReady = 'ORDER_READY',
  PendingOrderChoice = 'PENDING_ORDER_CHOICE',
  Undefined = 'UNDEFINED',
}

enum BotRuntimeMode {
  MultiTResetConfirmation = 'MULTI_T_RESET_CONFIRMATION',
  MultiOrder = 'MULTI_ORDER',
  EarlierOrder = 'EARLIER_ORDER',
  EndOfCandle = 'END_OF_CANDLE',
}

export type NextOrderInfo = {
  state: NextOrderState;
  currentCbr?: number;
  isCbrtReached: boolean;
  currentMultiOrderIndex?: number;
  multiOrderAmount?: number;
  nextWakeUpTimestamp: number | null;
  nextWakeUpMode: BotRuntimeMode | null;
  nextOrderType: 'BUY' | 'SELL';
};

export type BotProfits = {
  total: {
    stable: number;
    asset: number;
  };
  current: {
    stable: number | null;
    asset: number | null;
  };
  noAlgorithmicProgressionProfit: number;
};

export type Bot = {
  balance: {
    asset: {
      amount: number;
      worth: number;
    };
    stable: {
      amount: number;
      worth: number;
    };
    total: {
      stable: number;
      asset: number;
    };
  };
  baseAsset: string;
  botId: string;
  clusterId: string;
  isActive: boolean;
  isEnabled: boolean;
  quoteAsset: string;
  status: 'NEW' | 'SELLING' | 'SOLD' | 'BUYING' | 'BOUGHT';
  currentPrice: number;
  currentProfit: number;
  profitSinceStart: number;
  profit: {
    stable: number;
    asset: number;
  };
  profits: BotProfits | null;
  nextOrder: NextOrderInfo;
  subscriptionId: number;
};

export type ClusterInfo = {
  bots: Array<Bot>;
  totalValue: number;
  startBalance: number;
  profitSinceStart: number;
};

export type BotOverviewResponse = {
  clusters: Record<string, ClusterInfo>;
  totalValue: number;
  startBalance: number;
  profitSinceStart: number;
};

export type Transaction = {
  index?: string;
  id: number;
  botId: number;
  subscriptionId: number;
  baseAsset: string;
  quoteAsset: string;
  transactionIndex: number;
  compactTransactionId: string | null;
  timeFrames: BotTimeFrames;
  complexParams: BotComplexParams;
  orderId: number | null;
  orderType: 'BUY' | 'SELL';
  transactionType: string;
  orderPrice: number;
  assetQuantity: number;
  stableQuantity: number;
  currentAssetBalance: number;
  currentStableBalance: number;
  totalBalance: number;
  currentFee: number;
  totalFee: number;
  isComplete: number; // boolean;
  success: boolean | null;
  profit: number | null;
  currentProfit: number | null;
  transactionTimestamp: number;
  transactionDate: string;
  initialVolume: number;
  initialVolumeAsset: string;
  currentCbr: number | null;
  cbrt: number;
  shorterTimeFrameInterval: number;
  mrcEjectedPrice: number | null;
  mrcAcceptedPrice: number | null;
  multiOrderVolume: number | null;
  multiOrderIndex: number | null;
  customBalanceUsed: number | null;
  customBalanceSizeUsed: string | null;
  extraInfo: string | null;
  actions: string | null;
  priceChange?: number;
  averageOrderPrice?: number;
  candles?: {
    higher: Candle[];
    shorter: Candle[];
    multi: Candle[];
  };
  currentPrice?: number | null;
  type?: string;
  exchange: Exchange;
  signals?: IndicatorDataPoint[];
};

export type CustomTransaction = {
  id: number;
  botId: number;
  transactionIndex: number;
  transactionType: string;
  currentPrice: number;
  assetQuantity: number;
  stableQuantity: number;
  currentAssetBalance: number;
  currentStableBalance: number;
  totalBalance: number;
  currentFee: number;
  totalFee: number;
  transactionTimestamp: number;
  transactionDate: string;
  extraInfo: string | null;
  actions: string | null;
};

export type TransactionsResponse = Record<string, Transaction[]>;

export type ActionNeededProblem = {
  baseAsset?: string;
  clusterId?: string;
  botId?: string;
  reason:
    | 'FETCHING_DATA_PENDING'
    | 'ACTIVE_STATUS'
    | 'SYNC_STATUS'
    | 'GRTS_SERVICE_OFFLINE';
  extraInfo?: string;
};

export type HealthResponse = {
  problems: Array<ActionNeededProblem>;
};

export enum SortingToggleOptions {
  Diversity = 'Diversity',
  TotalValue = 'Total Value',
  TotalStableProfit = 'Total Stable Profit',
  TotalAssetProfit = 'Total Asset Profit',
  CurrentStableProfit = 'Current Stable Profit',
  CurrentAssetProfit = 'Current Asset Profit',
  NoAlgorithmicDifference = 'No Algorithmic Difference',
}

export enum SortingDirection {
  Descending = 'DESC',
  Ascending = 'ASC',
}

export enum OverviewChartTimeframe {
  OneWeek = '1W',
  OneMonth = '1M',
  TwoMonths = '2M',
  ThreeMonths = '3M',
  SixMonths = '6M',
  OneYear = '1Y',
  TwoYears = '2Y',
  All = 'ALL',
}

export enum OverviewChartType {
  FullBalance = 'FULL_BALANCE',
  ProfitOnly = 'PROFIT_ONLY',
}

export type SummaryInformation = {
  dayChange: number;
  dayChangeRelative: number;
  napDifference: number;
  napDifferenceRelative: number;
  amountOfDays: number;
};
